import React from 'react';
import { ArrowRight, Database, MessageSquare, Network } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslations } from '../context/TranslationContext';

const ProductsSection: React.FC = () => {
  const { t } = useTranslations();

  const products = [
    {
      icon: <Database size={32} />,
      serviceName: "LegisGraph",
      title: t("legisgraph_card_title"),
      description: t("legisgraph_card_description"),
      learnMoreUrl: "/legisgraph",
      iconColor: "text-mystic-violet",
    },
    {
      icon: <MessageSquare size={32} />,
      serviceName: t("legischat"),
      title: t("legischat_card_title"),
      description: t("legischat_card_description"),
      learnMoreUrl: "/legischat",
      iconColor: "text-nebula-pink",
    },
    {
      icon: <Network size={32} />,
      serviceName: "Doc2Graph",
      title: t("doc2graph_card_title"),
      description: t("doc2graph_card_description"),
      learnMoreUrl: "/doc2graph",
      iconColor: "text-cosmic-teal",
    },
  ];

  return (
    <section id="products" className="border-t border-white/10 py-16 md:py-24 bg-void-blue">
      {/* Main container */}
      <div className="mx-auto w-full max-w-7xl px-6 md:px-12">

        {/* Title Section */}
        <div className="mb-12 md:mb-16">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-4">
            {t("products_section_title")}
          </h2>
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-3xl">
            {t("products_section_subtitle")}
          </p>
        </div>

        {/* 3-column grid for product cards - Full width */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <Link
              key={index}
              to={product.learnMoreUrl}
              className="glass-panel rounded-2xl transition-all duration-300 p-6 md:p-8 flex flex-col group cursor-pointer border border-white/10 hover:border-cosmic-teal/40 hover:shadow-[0_0_30px_rgba(45,212,191,0.12)]"
            >
              {/* Icon and Service Name */}
              <div className="mb-5">
                <div className={`${product.iconColor} mb-4`}>
                  {product.icon}
                </div>
                <h3 className="text-2xl font-bold text-white group-hover:text-cosmic-teal transition-colors duration-200">
                  {product.serviceName}
                </h3>
              </div>

              {/* Title */}
              <h4 className="text-lg font-semibold text-gray-200 mb-3">
                {product.title}
              </h4>

              {/* Description */}
              <p className="text-gray-400 text-base leading-relaxed mb-6 flex-grow">
                {product.description}
              </p>

              {/* Learn More Button Badge */}
              <div className="mt-auto pt-2">
                <span className="inline-flex w-full items-center justify-between rounded-xl border border-white/10 bg-gradient-to-r from-cosmic-teal/20 to-transparent px-4 py-3 text-sm font-bold text-white transition-all duration-300 group-hover:border-white/20 group-hover:bg-white/10">
                  <span>{t("learn_more")}</span>
                  <ArrowRight size={16} aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
